const SMALL = { zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19 };
const TENS = { twenty: 20, thirty: 30, forty: 40, fifty: 50 };
const ONES = 'one|two|three|four|five|six|seven|eight|nine';
const HOUR = `(?:\\d{1,2}|twenty(?:[ -](?:one|two|three))?|${Object.keys(SMALL).sort((a, b) => b.length - a.length).join('|')})`;
const MINUTE = `(?:(?:twenty|thirty|forty|fifty)(?:[ -](?:${ONES}))?|(?:oh|zero)[ -](?:${ONES})|${Object.keys(SMALL).sort((a, b) => b.length - a.length).join('|')})`;
const MERIDIEM = '(?:a\\.?\\s*m\\.?|p\\.?\\s*m\\.?)';
const MINUTE_JOIN = '(?:\\s+|-(?=(?:twenty|thirty|forty|fifty|oh|zero)\\b))';
const END = '(?![\\w:])';
const QUANTITY = /^\s*(?:dollars?|bucks?|euros?|pounds?|percent|degrees|people|items|times|thousand|million)\b/i;

function number(value) {
  if (/^\d+$/.test(value)) return Number(value);
  const words = value.toLowerCase().replace(/-/g, ' ').trim().split(/\s+/);
  if (words.length === 1) return SMALL[words[0]] ?? TENS[words[0]];
  if (words.length === 2 && ['oh', 'zero'].includes(words[0])) return SMALL[words[1]];
  if (words.length === 2 && TENS[words[0]] !== undefined && SMALL[words[1]] < 10) return TENS[words[0]] + SMALL[words[1]];
  return NaN;
}

// Convert ONLY clock phrases. Never rewrite the stored transcript, prices,
// quantities, phone numbers, or dates into hours.
export function extractSpokenClocks(text) {
  const candidates = [], ignored = [];
  const collect = (pattern, read) => {
    for (const match of text.matchAll(pattern)) {
      const clock = { ...read(match), index: match.index, end: match.index + match[0].length, raw: match[0] };
      if (QUANTITY.test(text.slice(clock.end))) ignored.push(clock);
      else candidates.push(clock);
    }
  };
  collect(/\b(noon|midnight)\b/gi, match => ({ hour: match[1].toLowerCase() === 'noon' ? 12 : 0, minute: 0, absolute: true }));
  collect(new RegExp(`\\b(half|quarter)\\s+(past|to)\\s+(${HOUR}|noon|midnight)(?:\\s*(${MERIDIEM}))?${END}`, 'gi'), match => {
    const named = match[3].toLowerCase();
    return { hour: named === 'noon' ? 12 : named === 'midnight' ? 0 : number(named), minute: 0, delta: (match[2].toLowerCase() === 'to' ? -1 : 1) * (match[1].toLowerCase() === 'half' ? 30 : 15), meridiem: match[4], absolute: ['noon', 'midnight'].includes(named) };
  });
  collect(new RegExp(`\\b(?:at|around|by)\\s+(${HOUR})(?::(\\d{1,2})|${MINUTE_JOIN}(${MINUTE}))?(?:\\s+o['’]?clock)?(?:\\s*(${MERIDIEM}))?${END}`, 'gi'), match => ({ hour: number(match[1]), minute: match[2] ? Number(match[2]) : match[3] ? number(match[3]) : 0, meridiem: match[4], absolute: /^0\d/.test(match[1]) }));
  collect(new RegExp(`\\b(${HOUR})(?::(\\d{1,2})|${MINUTE_JOIN}(${MINUTE}))?\\s*(${MERIDIEM})${END}`, 'gi'), match => ({ hour: number(match[1]), minute: match[2] ? Number(match[2]) : match[3] ? number(match[3]) : 0, meridiem: match[4] }));
  collect(new RegExp(`\\b(\\d{1,2}):(\\d{1,2})(?:\\s*(${MERIDIEM}))?${END}`, 'gi'), match => ({ hour: Number(match[1]), minute: Number(match[2]), meridiem: match[3], absolute: /^0\d/.test(match[1]) }));
  collect(new RegExp(`\\b(${HOUR})\\s+o['’]?clock(?:\\s*(${MERIDIEM}))?${END}`, 'gi'), match => ({ hour: number(match[1]), minute: 0, meridiem: match[2] }));
  // Prefer the widest representation of overlapping matches, e.g. at2:30PM.
  const clocks = [];
  candidates.sort((a, b) => a.index - b.index || b.end - a.end).forEach(clock => {
    if (!clocks.some(other => clock.index < other.end && clock.end > other.index) && !ignored.some(other => clock.index < other.end && clock.end > other.index)) clocks.push(clock);
  });
  const unsupported = !clocks.length && !ignored.length && new RegExp(`\\b(?:at|around|by)\\s+(?:\\d{1,2}(?!\\d)|${[...Object.keys(SMALL), ...Object.keys(TENS), 'half', 'quarter'].join('|')})(?:ish)?\\b`, 'i').test(text);
  const continuation = new RegExp(`^\\s*(?:and\\s+|or\\s+|to\\s+|[-–—/]\\s*)(${HOUR})(?::\\d{1,2}|\\s+${MINUTE})?(?:\\s*${MERIDIEM})?${END}`, 'i');
  const compactConflict = clocks.some(clock => {
    const match = text.slice(clock.end).match(continuation);
    return !!match && !QUANTITY.test(text.slice(clock.end + match[0].length));
  });
  return { clocks, ignored, unsupported, compactConflict };
}

function contextWindow(text) {
  // Travel can legitimately happen at any hour. Do not infer airport/flight
  // departures from business hours; an explicit morning/PM cue still wins.
  if (/\b(?:airport|flight|train|overnight)\b/i.test(text)) return null;
  const choices = [
    [/\b(?:dinner|supper)\b/i, 16, 23],
    [/\b(?:lunch|luncheon)\b/i, 11, 15],
    [/\bbreakfast\b/i, 5, 11],
    [/\b(?:dentist|doctor|clinic|appointment|meeting|interview|haircut|conference|call|phone|office|client|invoice|proposal|bank|post office)\b/i, 8, 18.01],
    [/\b(?:pharmacy|prescription|dry cleaning|shopping|groceries|buy|order|ink toner|supplies)\b/i, 8, 20],
  ].map(([pattern, from, until]) => { const match = text.match(pattern); return match ? { index: match.index, from, until } : null; }).filter(Boolean);
  return choices.sort((a, b) => a.index - b.index)[0] || null;
}

export function resolveSpokenClock(clock, text) {
  const { hour, minute = 0 } = clock;
  if (!Number.isInteger(hour) || hour < 0 || hour > 23 || !Number.isInteger(minute) || minute < 0 || minute > 59) return null;
  let resolved;
  const marker = clock.meridiem?.toLowerCase().replace(/[.\s]/g, '');
  if (marker) {
    if (hour < 1 || hour > 12) return null;
    resolved = hour % 12 + (marker === 'pm' ? 12 : 0);
  } else if (clock.absolute || hour === 0 || hour > 12) resolved = hour;
  else {
    const periods = [...new Set((text.toLowerCase().match(/\b(?:morning|afternoon|evening|night|tonight)\b/g) || []).map(p => p === 'tonight' ? 'night' : p))];
    if (periods.length > 1) return null;
    if (periods[0] === 'morning') resolved = hour % 12;
    else if (periods[0] === 'afternoon') resolved = hour % 12 + 12;
    else if (periods[0] === 'evening') { if (hour === 12) return null; resolved = hour + 12; }
    else if (periods[0] === 'night') resolved = hour === 12 ? 0 : hour <= 5 ? hour : hour + 12;
    else {
      const window = contextWindow(text);
      if (!window) return null;
      const choices = [hour % 12, hour % 12 + 12].filter(candidate => candidate + minute / 60 >= window.from && candidate + minute / 60 < window.until);
      if (choices.length !== 1) return null;
      resolved = choices[0];
    }
  }
  const minutes = resolved * 60 + minute + (clock.delta || 0);
  return { hour: ((Math.floor(minutes / 60) % 24) + 24) % 24, minute: ((minutes % 60) + 60) % 60, dayShift: Math.floor(minutes / 1440), baseHour: resolved };
}